import { OpenFeature } from '@openfeature/react-sdk';
import { OpenFeatureSplitProvider } from '@splitsoftware/openfeature-web-split-provider';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';

import * as configurationManager from '../config/configurationManager';

interface FeatureFlagContext {
    targetingKey: string;
}

let isInitialized: boolean = false;
let splitClient;

// Feature flag keys
export const FEATURE_FLAGS = {
    VALIDATION_ENABLED: 'rms_backoffice_store_property_validation',
    PRINTING_TEMPLATE_IMPORT_VALIDATION: 'rms_backoffice_printing_template_import_validation',
    INVENTORY_COST_CALCULATION_ENABLED: 'rms_stock_item_recipe_price_issue',
    SHOW_MARKETING_CONSENT_CHECKBOX: 'rms_pos_show_marketing_consent_checkbox',
    DAILY_REPORT_FLIPDISH_PROCESSING_FEE: 'rms_pos_daily_report_flipdish_processing_fee',
    FLIPDISH_AUTO_ACCEPT_REQUIRES_POS: 'rms_backoffice_flipdish_auto_accept_requires_pos'
} as const;

/**
 * Initializes the OpenFeature SDK with Harness provider (via Split SDK).
 * Should be called once before the app renders.
 * @param context - Context for targeting with a unique identifier
 */
export async function initializeOpenFeature(context: FeatureFlagContext = { targetingKey: 'anonymous' }): Promise<void> {
    if (isInitialized) {
        return;
    }

    const config = configurationManager.getConfig();

    try {
        const splitFactory = SplitFactory({
            core: {
                authorizationKey: config.rmsFrontendSplitIoApiKey,
                key: context.targetingKey
            }
        });

        // Wait for the Split SDK client to be ready
        splitClient = splitFactory.client();
        
        await new Promise<void>((resolve, reject) => {
            splitClient.on(splitClient.Event.SDK_READY, () => {
                resolve();
            });

            splitClient.on(splitClient.Event.SDK_READY_FROM_CACHE, () => {
                resolve();
            });
            
            splitClient.on(splitClient.Event.SDK_READY_TIMED_OUT, () => {
                console.error('Split SDK timed out');
                reject(new Error('SDK_READY_TIMED_OUT'));
            });
        });

        const openFeatureProvider = new OpenFeatureSplitProvider(splitFactory);
        OpenFeature.setProvider(openFeatureProvider);
        isInitialized = true;
    } catch (error) {
        console.error('Failed to initialize OpenFeature with Split provider:', error);
    }
}

/**
 * Gets the OpenFeature client for evaluating flags
 * @returns The OpenFeature client
 */
export function getFeatureFlagClient() {
    return OpenFeature.getClient();
}

/**
 * Checks if OpenFeature has been initialized
 * @returns Whether OpenFeature is initialized
 */
export function isOpenFeatureInitialized(): boolean {
    return isInitialized;
}

/**
 * Gets a feature flag value at runtime using OpenFeature client
 * @param flagKey - The feature flag key
 * @param defaultValue - Default value if SDK is not initialized
 * @returns Promise resolving to the flag value
 */
export function getBooleanFlag(flagKey: string, defaultValue: boolean = false): boolean {
    if (!isInitialized) {
        return defaultValue;
    }
    
    try {
        const client = OpenFeature.getClient();
        const flagValue = client.getBooleanValue(flagKey, defaultValue);
        return flagValue;
    } catch (error) {
        console.error('Failed to evaluate feature flag:', flagKey, error);
        return defaultValue;
    }
}
